<script setup lang="ts">
  import { TooltipProvider, TooltipRoot } from "reka-ui"
  import { computed, ref, useTemplateRef } from "vue"

  import TooltipContent from "./PlainTooltipContent.vue"
  import TooltipTrigger from "./PlainTooltipTrigger.vue"

  interface TooltipProps {
    defaultOpen?: boolean
    open?: boolean
    triggerLabel: string
  }

  const props = withDefaults(defineProps<TooltipProps>(), {
    defaultOpen: false,
    open: undefined,
  })

  const isInternalOpenRef = ref(props.open === undefined && props.defaultOpen)
  const isPinnedByClickRef = ref(false)
  const announcementRef = ref("")
  const textSourceRef = useTemplateRef<HTMLElement>("textSource")

  // Always a boolean: reka-ui treats `undefined` as uncontrolled and its state would drift from ours.
  const isTooltipOpen = computed(() => {
    return isInternalOpenRef.value || !!props.open
  })

  function close() {
    isInternalOpenRef.value = false
    isPinnedByClickRef.value = false
    announcementRef.value = ""
  }

  function toggleOnClick() {
    if (isPinnedByClickRef.value) {
      close()
      return
    }
    isInternalOpenRef.value = true
    isPinnedByClickRef.value = true
    announcementRef.value = textSourceRef.value?.textContent?.trim() ?? ""
  }

  // reka-ui still emits this for taps on the trigger, just pre-prevented.
  function onPointerDownOutside(event: Event) {
    if (!event.defaultPrevented) close()
  }

  function onOpenChange(isOpen: boolean) {
    if (isOpen) {
      isInternalOpenRef.value = true
      return
    }
    // Pinned: ignore reka's move-off/scroll closes; blur, Escape and outside taps still close it.
    if (!isPinnedByClickRef.value) close()
  }

  defineOptions({
    inheritAttrs: false,
  })
</script>

<template>
  <TooltipProvider>
    <TooltipRoot
      :delay-duration="400"
      :open="isTooltipOpen"
      data-testid="plain-tooltip"
      disable-closing-trigger
      @update:open="onOpenChange"
    >
      <TooltipTrigger
        :aria-label="props.triggerLabel"
        :aria-expanded="isTooltipOpen"
        @click.prevent="toggleOnClick"
        @blur="close"
      >
        <slot name="trigger" />
      </TooltipTrigger>
      <TooltipContent
        @escape-key-down="close"
        @pointer-down-outside="onPointerDownOutside"
      >
        <slot />
      </TooltipContent>
    </TooltipRoot>
    <Teleport to="body">
      <!-- Teleported so the copy stays out of the caller's text -->
      <span ref="textSource" hidden><slot /></span>
      <span
        class="sr-only"
        aria-live="polite"
        data-testid="plain-tooltip-announcer"
      >
        {{ announcementRef }}
      </span>
    </Teleport>
  </TooltipProvider>
</template>
